import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Download } from 'lucide-react';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect } from '@/components/ui/Input';
import { cn } from '@/lib/cn';
import { toCsv, downloadCsv } from '@/lib/csv';
import { fetchWaitInsights } from './api';
import { drName, fmtMin } from './DashCard';
import type { InsightSegmentKey, InsightStats, InsightWindow, WaitInsightRow, WaitInsights } from './types';

/**
 * Dashboard → "Avg. wait time" → View insights: where each OPD patient's time
 * went at every stage. Opens in place of the Dashboard (like View alerts opens
 * Pharmacy), and App.tsx gives a visit opened from here a link back. Rules
 * (segments, the 4-hour outlier cap) are the server's —
 * backend/utils/waitInsights.js — so "Intake → doctor" here is the card's figure.
 */

const SEGMENT_STYLE: Record<InsightSegmentKey, { solid: string; label: string }> = {
  intake:       { solid: 'bg-tone-violet', label: 'Intake' },
  vitals:       { solid: 'bg-tone-green',  label: 'Vitals' },
  assessment:   { solid: 'bg-tone-blue',   label: 'Assessment' },
  wait_doctor:  { solid: 'bg-tone-orange', label: 'Waiting for doctor' },
  consultation: { solid: 'bg-tone-teal',   label: 'Consultation' },
  exit:         { solid: 'bg-tone-slate',  label: 'After consultation' },
};
const SEGMENT_ORDER: InsightSegmentKey[] = ['intake', 'vitals', 'assessment', 'wait_doctor', 'consultation', 'exit'];
const RESOLUTION_LABEL: Record<string, string> = { closed: 'Closed', followup: 'Follow-up', referred: 'Referred', admitted: 'Admitted' };

const timeOf = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—');
const dateOf = (iso: string) => new Date(iso).toLocaleDateString([], { day: '2-digit', month: 'short' });
const sameDay = (a: string, b: string) => new Date(a).toDateString() === new Date(b).toDateString();

// Filters survive opening a visit and coming back.
const STATE_KEY = 'hms_wait_insights_state';
type StatusFilter = '' | 'closed' | 'open' | 'not_closed';
interface SavedState { range: InsightWindow; search: string; doctor: string; status: StatusFilter }
function loadState(): SavedState {
  try {
    const s = JSON.parse(sessionStorage.getItem(STATE_KEY) || '{}') as Partial<SavedState>;
    const status: StatusFilter = s.status === 'closed' || s.status === 'open' || s.status === 'not_closed' ? s.status : '';
    return { range: s.range === 'today' || s.range === '30d' ? s.range : '7d', search: s.search ?? '', doctor: s.doctor ?? '', status };
  } catch { return { range: '7d', search: '', doctor: '', status: '' }; }
}

/** One bar segment whose width is its share — set through a ref (features/** bans inline style). */
function BarPart({ grow, className, title }: { grow: number; className: string; title: string }) {
  const ref = useCallback((el: HTMLDivElement | null) => { el?.style.setProperty('flex-grow', String(grow)); }, [grow]);
  return <div ref={ref} title={title} className={cn('h-full basis-0', className)} />;
}

function Stat({ label, stats, hint, emphasis, of }: { label: string; stats: InsightStats; hint?: string; emphasis?: boolean; of: number }) {
  return (
    <div className={cn('rounded-lg border border-line bg-card p-3', emphasis && 'border-tone-violet/40 bg-tone-violet-soft')}>
      <div className="text-[11.5px] font-semibold text-ink-muted">{label}</div>
      <div className={cn('mt-1 text-[22px] font-bold leading-none', emphasis ? 'text-tone-violet' : 'text-ink')}>{fmtMin(stats.avg)}</div>
      <div className="mt-1.5 text-[11px] text-ink-muted">
        {stats.n ? `Median ${fmtMin(stats.median)} · 90% within ${fmtMin(stats.p90)}` : 'No data'}
      </div>
      {hint && <div className="mt-1 text-[11px] text-ink-muted">{hint}</div>}
      {/* Each figure is averaged over the visits that have it — say how many, so tiles
          built on different visits are never read as parts of one whole. */}
      <div className={cn('mt-1.5 inline-block rounded px-1.5 text-[11px] font-semibold',
        of && stats.n / of < 0.5 ? 'bg-warn-soft text-warn-ink' : 'bg-muted text-ink-muted')}>
        Based on {stats.n} of {of} visits
      </div>
    </div>
  );
}

/** A minutes cell: highlights the patient's longest wait, marks live and left-open values. */
function MinCell({ row, k }: { row: WaitInsightRow; k: InsightSegmentKey }) {
  const m = row.segments[k];
  if (m == null) return <span className="text-ink-muted">—</span>;
  const longest = row.longest_wait?.key === k;
  const live = row.live_segments.includes(k);
  const leftOpen = row.excluded_from_averages.includes(k);
  if (leftOpen) {
    return (
      <span className="inline-flex flex-col items-end leading-tight" title="Open for more than 4 hours — this stage was most likely left open and closed later, so it is not real waiting and is left out of the averages.">
        <span className="tabular-nums text-ink-muted line-through decoration-ink-muted/40">{fmtMin(m)}</span>
        <span className="mt-0.5 rounded bg-warn-soft px-1 text-[10px] font-semibold text-warn-ink">left open</span>
      </span>
    );
  }
  return (
    <span title={live ? 'Still in progress' : undefined}
      className={cn('tabular-nums', longest && 'rounded bg-tone-orange-soft px-1.5 py-0.5 font-semibold text-tone-orange')}>
      {fmtMin(m)}{live ? ' …' : ''}
    </span>
  );
}

interface Props {
  onBack: () => void;
  onOpenVisit?: (visitCode: string) => void;
}

export function WaitInsightsPage({ onBack, onOpenVisit }: Props) {
  const initial = useMemo(loadState, []);
  const [range, setRange] = useState<InsightWindow>(initial.range);
  const [search, setSearch] = useState(initial.search);
  const [doctor, setDoctor] = useState(initial.doctor);
  const [status, setStatus] = useState<StatusFilter>(initial.status);
  const [data, setData] = useState<WaitInsights | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);

  useEffect(() => {
    try { sessionStorage.setItem(STATE_KEY, JSON.stringify({ range, search, doctor, status })); } catch { /* private window */ }
  }, [range, search, doctor, status]);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError('');
    fetchWaitInsights(range)
      .then(d => { if (alive) setData(d); })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [range, reload]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.rows ?? []).filter(r =>
      (!doctor || (r.doctor_name || 'Unassigned') === doctor)
      && (!status || (status === 'closed' ? !r.in_progress : status === 'open' ? r.in_progress && r.started_today : r.in_progress && !r.started_today))
      && (!q || [r.patient_name, r.uhid, r.visit_code, r.token != null ? `#${r.token}` : ''].some(v => v?.toLowerCase().includes(q))));
  }, [data, search, doctor, status]);

  const s = data?.summary;
  const doctors = s?.by_doctor.map(d => d.doctor) ?? [];

  const columns: DataTableColumn<WaitInsightRow>[] = [
    {
      key: 'patient', header: 'Patient', sortable: true, sortValue: r => r.patient_name ?? '',
      render: r => (
        <div className="min-w-[140px]">
          <div className="font-semibold text-ink">{r.patient_name || '—'}</div>
          <div className="text-[11px] text-ink-muted">
            {r.uhid}{r.token != null ? ` · Token ${r.token}` : ''}{r.appointment ? ' · Appt' : ' · Walk-in'}
          </div>
          {(r.in_progress || (r.priority && r.priority !== 'Normal') || r.hold_count > 0) && (
            <div className="mt-0.5 flex flex-wrap gap-1">
              {r.in_progress && <Badge variant="info">In progress</Badge>}
              {r.priority && r.priority !== 'Normal' && <Badge variant="danger">{r.priority}</Badge>}
              {r.hold_count > 0 && <Badge variant="warn">Held ×{r.hold_count}</Badge>}
            </div>
          )}
        </div>
      ),
    },
    { key: 'doctor', header: 'Doctor', sortable: true, sortValue: r => r.doctor_name ?? '', render: r => (r.doctor_name ? drName(r.doctor_name) : <span className="text-ink-muted">Unassigned</span>) },
    { key: 'arrived', header: 'Arrived', sortable: true, sortValue: r => r.arrived_at, render: r => <span className="whitespace-nowrap">{dateOf(r.arrived_at)}, {timeOf(r.arrived_at)}</span> },
    ...SEGMENT_ORDER.map((k): DataTableColumn<WaitInsightRow> => ({
      key: k, header: SEGMENT_STYLE[k].label, align: 'right', sortable: true,
      sortValue: r => r.segments[k], render: r => <MinCell row={r} k={k} />,
    })),
    {
      key: 'i2d', header: 'Intake → doctor', align: 'right', sortable: true, sortValue: r => r.intake_to_doctor_min,
      render: r => <span className="font-semibold tabular-nums text-tone-violet">{fmtMin(r.intake_to_doctor_min)}</span>,
    },
    {
      key: 'total', header: 'Total visit', align: 'right', sortable: true, sortValue: r => r.total_visit_min,
      render: r => (r.excluded_from_averages.includes('total_visit') ? (
        <span className="inline-flex flex-col items-end leading-tight" title="Over 12 hours — a stage was left open, so this is not the real visit length and is left out of the averages.">
          <span className="tabular-nums text-ink-muted line-through decoration-ink-muted/40">{fmtMin(r.total_visit_min)}</span>
          <span className="mt-0.5 rounded bg-warn-soft px-1 text-[10px] font-semibold text-warn-ink">left open</span>
        </span>
      ) : <span className="font-semibold tabular-nums">{fmtMin(r.total_visit_min)}{r.in_progress && r.total_visit_min != null ? ' …' : ''}</span>),
    },
    {
      key: 'left', header: 'Left / outcome', sortable: true, sortValue: r => r.resolved_at,
      render: r => (r.in_progress ? <span className="text-ink-muted">Still here</span> : (
        <div className="whitespace-nowrap leading-tight">
          <div>{r.resolved_at && !sameDay(r.resolved_at, r.arrived_at) ? `${dateOf(r.resolved_at)}, ` : ''}{timeOf(r.resolved_at)}</div>
          <div className="text-[11px] text-ink-muted">{RESOLUTION_LABEL[r.resolution ?? ''] ?? (r.resolution || 'Closed')}</div>
        </div>
      )),
    },
    {
      key: 'longest', header: 'Longest wait', sortable: true, sortValue: r => r.longest_wait?.minutes,
      render: r => (r.longest_wait ? <span className="whitespace-nowrap">{SEGMENT_STYLE[r.longest_wait.key].label}</span> : '—'),
    },
  ];

  // Excel-friendly local time ("23-10-2026 18:48"); raw ISO strings import as text.
  const stamp = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
  };
  const exportCsv = () => {
    const flat = rows.map(r => ({
      patient: r.patient_name, uhid: r.uhid, visit: r.visit_code, token: r.token, doctor: r.doctor_name,
      type: r.appointment ? 'Appointment' : 'Walk-in', arrived: stamp(r.arrived_at), doctor_started: stamp(r.consultation_started_at),
      left: stamp(r.resolved_at), outcome: RESOLUTION_LABEL[r.resolution ?? ''] ?? (r.resolution || ''),
      intake: r.segments.intake, vitals: r.segments.vitals, assessment: r.segments.assessment,
      wait_doctor: r.segments.wait_doctor, consultation: r.segments.consultation, exit: r.segments.exit,
      intake_to_doctor: r.intake_to_doctor_min, total: r.total_visit_min, longest: r.longest_wait?.label ?? '',
      left_open: r.excluded_from_averages.join(' '), holds: r.hold_count, in_progress: r.in_progress ? 'yes' : 'no',
    }));
    downloadCsv(`wait-time-insights-patients-${range}-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(flat, [
      ['patient', 'Patient'], ['uhid', 'UHID'], ['visit', 'Visit'], ['token', 'Token'], ['doctor', 'Doctor'], ['type', 'Type'],
      ['arrived', 'Arrived'], ['doctor_started', 'Doctor started'], ['left', 'Left'], ['outcome', 'Outcome'],
      ['intake', 'Intake (min)'], ['vitals', 'Vitals (min)'], ['assessment', 'Assessment (min)'], ['wait_doctor', 'Waiting for doctor (min)'],
      ['consultation', 'Consultation (min)'], ['exit', 'After consultation (min)'], ['intake_to_doctor', 'Intake to doctor (min)'],
      ['total', 'Total visit (min)'], ['longest', 'Longest wait'], ['left_open', 'Left open (not in averages)'],
      ['holds', 'Times held'], ['in_progress', 'In progress'],
    ]));
  };

  const barSegments = s?.segments.filter(x => x.avg) ?? [];

  return (
    <div className="fade-in">
      <div className="mb-4 flex flex-wrap items-start gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="mr-1 h-3.5 w-3.5" /> Dashboard</Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-[18px] font-bold text-ink">Wait-time insights</h1>
          <p className="mt-0.5 max-w-[760px] text-[12.5px] text-ink-muted">
            Where each OPD patient&apos;s time went at every stage, from intake to leaving. The Dashboard&apos;s
            &ldquo;Avg. wait time&rdquo; is <b>Intake → doctor</b>: from the start of intake until the doctor starts the consultation.
          </p>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SegmentedControl<InsightWindow> size="sm" value={range} onChange={setRange}
          options={[{ value: 'today', label: 'Today' }, { value: '7d', label: 'Last 7 days' }, { value: '30d', label: 'Last 30 days' }]} />
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Input aria-label="Search patients" placeholder="Search patient, UHID, token" value={search}
            onChange={e => setSearch(e.target.value)} className="h-8 w-56 text-[12.5px]" />
          <NativeSelect aria-label="Filter by doctor" value={doctor} onChange={e => setDoctor(e.target.value)} className="h-8 w-48 text-[12.5px]">
            <option value="">All doctors</option>
            {doctors.map(d => <option key={d} value={d}>{d === 'Unassigned' ? d : drName(d)}</option>)}
          </NativeSelect>
          <NativeSelect aria-label="Filter by visit status" value={status} onChange={e => setStatus(e.target.value as StatusFilter)} className="h-8 w-44 text-[12.5px]">
            <option value="">All visits</option>
            <option value="closed">Closed</option>
            <option value="open">Still in progress today</option>
            <option value="not_closed">Never closed (earlier days)</option>
          </NativeSelect>
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded-md bg-danger-soft p-3 text-[12.5px] text-danger-ink">
          Could not load insights: {error}{' '}
          <button type="button" className="underline" onClick={() => setReload(n => n + 1)}>Try again</button>
        </div>
      )}

      {s && (
        <>
          {s.not_closed > 0 && (
            <div className="mb-3 flex flex-wrap items-start gap-2 rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-[12.5px] text-warn-ink">
              <span className="font-semibold">{s.not_closed} of {s.visits} visits {s.not_closed === 1 ? 'is from an earlier day and was' : 'are from earlier days and were'} never closed in the system.</span>
              <span>
                They have no total visit time, and any stage left open is not counted. So each figure below is averaged
                over a different number of visits — compare them with care. Close visits at Resolve to make these figures reliable.
              </span>
              <button type="button" className="font-semibold underline" onClick={() => setStatus('not_closed')}>Show them</button>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat of={s.visits} label="Intake → doctor (Dashboard figure)" stats={s.intake_to_doctor} emphasis hint="From intake until the doctor starts" />
            <Stat of={s.visits} label="Waiting for doctor" stats={s.wait_doctor} hint="In the queue, after vitals / assessment" />
            <Stat of={s.visits} label="Consultation" stats={s.consultation} hint="Doctor starts → doctor finishes" />
            <Stat of={s.visits} label="Total visit" stats={s.total_visit} hint="Intake to leaving — closed visits only" />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[1.4fr_1fr]">
            <div className="rounded-lg border border-line bg-card p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="text-[12.5px] font-semibold text-ink">Where the average visit&apos;s time goes</div>
                {s.biggest_wait && (
                  <div className="text-[12px] text-ink-muted">
                    Biggest wait: <span className="font-semibold text-tone-orange">{s.biggest_wait.label}</span>
                    {' '}— {fmtMin(s.biggest_wait.avg)} on average{s.biggest_wait.share_pct != null ? ` (${s.biggest_wait.share_pct}% of the visit)` : ''}
                  </div>
                )}
              </div>
              {barSegments.length ? (
                <>
                  <div className="mt-2 flex h-4 w-full overflow-hidden rounded-full bg-muted">
                    {barSegments.map(x => (
                      <BarPart key={x.key} grow={x.avg ?? 0} className={SEGMENT_STYLE[x.key].solid} title={`${x.label}: ${fmtMin(x.avg)}`} />
                    ))}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-ink-muted">
                    {s.segments.map(x => (
                      <span key={x.key} className="inline-flex items-center gap-1.5">
                        <span className={cn('h-2.5 w-2.5 rounded-sm', SEGMENT_STYLE[x.key].solid)} />
                        {x.label}: <span className="font-semibold text-ink">{fmtMin(x.avg)}</span>
                        {x.share_pct != null && <span>({x.share_pct}%)</span>}
                      </span>
                    ))}
                  </div>
                </>
              ) : <div className="mt-2 text-[12px] text-ink-muted">No timed visits in this period.</div>}
            </div>

            {s.by_doctor.length > 0 && (
              <div>
                <DataTable
                  density="compact" appearance="sheet"
                  rows={s.by_doctor} rowKey={d => d.doctor}
                  initialSort={{ key: 'visits', direction: 'desc' }}
                  columns={[
                    { key: 'doctor', header: 'Doctor', sortable: true, sortValue: d => d.doctor, render: d => (d.doctor === 'Unassigned' ? d.doctor : drName(d.doctor)) },
                    { key: 'visits', header: 'Visits', align: 'right', sortable: true, sortValue: d => d.visits, render: d => d.visits },
                    { key: 'wait', header: 'Waiting for doctor', align: 'right', sortable: true, sortValue: d => d.avg_wait_doctor_min, render: d => fmtMin(d.avg_wait_doctor_min) },
                    { key: 'consult', header: 'Consultation', align: 'right', sortable: true, sortValue: d => d.avg_consultation_min, render: d => fmtMin(d.avg_consultation_min) },
                    { key: 'total', header: 'Total visit', align: 'right', sortable: true, sortValue: d => d.avg_total_visit_min, render: d => fmtMin(d.avg_total_visit_min) },
                  ]}
                />
              </div>
            )}
          </div>

          <div className="mb-1.5 mt-5 flex flex-wrap items-baseline justify-between gap-2">
            <div className="flex items-center gap-3">
              <div className="text-[12.5px] font-semibold text-ink">
                Patients ({rows.length}{data && data.total_rows > data.rows.length ? ` of ${data.total_rows}, latest ${data.rows.length} shown` : ''})
              </div>
              <Button variant="ghost" size="sm" onClick={exportCsv} disabled={!rows.length}
                title="Downloads the rows shown below (search and doctor filter applied) as a CSV file that opens in Excel">
                <Download className="mr-1 h-3.5 w-3.5" /> Export patients
              </Button>
            </div>
            <div className="text-[11px] text-ink-muted">
              <span className="rounded bg-tone-orange-soft px-1 font-semibold text-tone-orange">Highlighted</span> = that patient&apos;s longest wait ·
              {' '}… = still running ·{' '}
              <span className="rounded bg-warn-soft px-1 font-semibold text-warn-ink">left open</span> = stage open over 4 h, not counted in averages ·
              {' '}click a row to open the visit
            </div>
          </div>
        </>
      )}
      <DataTable
        density="compact"
        appearance="sheet"
        rows={rows}
        rowKey={r => r.visit_id}
        columns={columns}
        isLoading={loading && !data}
        initialSort={{ key: 'arrived', direction: 'desc' }}
        onRowClick={onOpenVisit ? r => onOpenVisit(r.visit_code) : undefined}
        emptyLabel="No OPD visits in this period"
        emptyHint={search || doctor ? 'Try clearing the search or doctor filter.' : undefined}
      />
    </div>
  );
}
